import React from 'react';
import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import STRING_KEYS from '../../../language/keys';
import PaginationResponseDto from '../../../interfaces/paginationResponse.interface';
import { Referral } from '../../../@types/Referral';
import { ReferralsRowItem } from '../ReferralsRowItem';
import { PendingReferralAction } from '../referrals.types';
import style from './style';

type ReferralsTableProps = {
  referralsData?: PaginationResponseDto<Referral>,
  isLoading: boolean,
  errorMessage?: string,
  page: number,
  rowsPerPage: number,
  onPageChange: (newPageNumber: number) => void,
  onAction: (action: PendingReferralAction) => void,
};

export const ReferralsTable = ({
  referralsData,
  isLoading,
  errorMessage,
  page,
  rowsPerPage,
  onPageChange,
  onAction,
}: ReferralsTableProps): JSX.Element => {
  const { t: translate } = useTranslation();

  const headers = [
    STRING_KEYS.REFERRALS.TABLE_HEADER_REFERRER,
    STRING_KEYS.REFERRALS.TABLE_HEADER_FRIEND_COMPANY,
    STRING_KEYS.REFERRALS.TABLE_HEADER_CREATED,
    STRING_KEYS.REFERRALS.TABLE_HEADER_PATH,
    STRING_KEYS.REFERRALS.TABLE_HEADER_STATUS,
    STRING_KEYS.REFERRALS.TABLE_HEADER_ELIGIBILITY,
    STRING_KEYS.REFERRALS.TABLE_HEADER_LINKED_ACCOUNT,
    STRING_KEYS.REFERRALS.TABLE_HEADER_REWARDS,
    STRING_KEYS.REFERRALS.TABLE_HEADER_POSITION,
  ];
  const columnsCount = headers.length + 1;

  const handleChangePage = (
    e: React.MouseEvent<HTMLButtonElement> | null,
    newPageNumber: number,
  ): void => {
    onPageChange(newPageNumber + 1);
  };

  const renderMessageRow = (content: JSX.Element) => (
    <TableRow>
      <TableCell align="center" colSpan={ columnsCount } sx={ style.messageCell.sx }>
        { content }
      </TableCell>
    </TableRow>
  );

  const renderBody = () => {
    if (isLoading) {
      return renderMessageRow(<CircularProgress />);
    }

    if (errorMessage) {
      return renderMessageRow(
        <Typography color="error">
          { translate(STRING_KEYS.REFERRALS.LOAD_ERROR, { message: errorMessage }) }
        </Typography>,
      );
    }

    if (!referralsData?.results.length) {
      return renderMessageRow(
        <Typography color="textSecondary">
          { translate(STRING_KEYS.REFERRALS.EMPTY) }
        </Typography>,
      );
    }

    return referralsData.results.map((referral: Referral) => (
      <ReferralsRowItem key={ referral.id } referral={ referral } onAction={ onAction } />
    ));
  };

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          { headers.map((header) => (
            <TableCell key={ header }>{ translate(header) }</TableCell>
          )) }
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        { renderBody() }
      </TableBody>
      <TableFooter>
        <TableRow>
          <TablePagination
            count={ referralsData?.totalCount || 0 }
            page={ page - 1 }
            rowsPerPage={ rowsPerPage }
            colSpan={ columnsCount }
            onPageChange={ handleChangePage }
            rowsPerPageOptions={ [] }
          />
        </TableRow>
      </TableFooter>
    </Table>
  );
};

ReferralsTable.defaultProps = {
  referralsData: undefined,
  errorMessage: undefined,
};
